import { makeVar } from '@apollo/client';
import { createError } from '@common/neo.tax';
import { FileWithUploadProgress } from '@ui-components/neo.tax';

import { validateFile } from '@neo.tax/shared/utils';

type FileName = string;

export type ResumableFileUpload = {
  file: File;
  /**
   * When string, the upload is in progress.
   */
  cloudStorageLocationUrl: string | null;
  uploadPercent: number;
  /**
   * An optional tag that can be used to query for files used in a specific file upload context.
   */
  tag?: string;

  status: 'pending' | 'success' | 'error';
  errorMessage?: string;
};

type FileUploadsState = Map<FileName, ResumableFileUpload>;

export const fileUploadsVar = makeVar<FileUploadsState>(new Map<FileName, ResumableFileUpload>());

export const has = (fileName: FileName): boolean => fileUploadsVar().has(fileName);

export const get = (fileName: FileName): ResumableFileUpload | undefined => fileUploadsVar().get(fileName);

export const getFileUploads = (tag?: string): ResumableFileUpload[] => {
  const fileUploads = fileUploadsVar();
  return [...fileUploads.values()].filter((fileUpload) => fileUpload.tag === tag);
};

export const addFileUpload = async (file: File, tag?: string): Promise<void> => {
  await addManyFileUploads([file], tag);
};

export const addManyFileUploads = async (files: File[], tag?: string): Promise<void> => {
  const fileUploads = new Map(fileUploadsVar());
  for (const file of files) {
    if (fileUploads.has(file.name)) {
      continue;
    }
    const { errorMessage, status } = await validateFile(file);

    fileUploads.set(file.name, {
      file,
      cloudStorageLocationUrl: null,
      uploadPercent: 0,
      tag,
      status,
      errorMessage,
    });
  }
  fileUploadsVar(fileUploads); // trigger publishing update to subscribers
};

export const removeFileUpload = (fileName: FileName): void => {
  const fileUploads = new Map(fileUploadsVar());
  fileUploads.delete(fileName);
  fileUploadsVar(fileUploads); // trigger publishing update to subscribers
};

export const updateFileUpload = (updatedFileUpload: ResumableFileUpload): void => {
  const fileName = updatedFileUpload.file.name;
  const fileUploads = new Map(fileUploadsVar());
  const fileUpload = fileUploads.get(fileName);
  if (!fileUpload) {
    throw createError('FileUploadError', 'Attempted to update a file upload that does not exist.');
  }
  fileUploads.set(fileName, updatedFileUpload);
  fileUploadsVar(fileUploads); // trigger publishing update to subscribers
};

export const setUploadPercent = (fileName: string, percent: number): boolean => {
  const fileUpload = get(fileName);
  if (!fileUpload) {
    return false;
  }
  fileUpload.uploadPercent = percent;
  updateFileUpload(fileUpload);
  return true;
};

export const getFilesWithUploadProgressForDisplaySection = (tag?: string): FileWithUploadProgress[] => {
  const fileUploads = getFileUploads(tag);
  return [...fileUploads.values()].map(({ cloudStorageLocationUrl, errorMessage, file, status, uploadPercent }) => ({
    fileName: file.name,
    uploadPercent: cloudStorageLocationUrl === null || uploadPercent === 100 ? null : uploadPercent,
    status,
    errorMessage,
  }));
};

export const isUploading = (tag?: string): boolean => {
  const fileUploads = getFileUploads(tag);
  return fileUploads.some((fileUpload) => typeof fileUpload.cloudStorageLocationUrl === 'string');
};
